import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import BenefitsBrandPanel from "../../components/BenefitsBrandPanel";
import { START_BENEFITS } from "../../utils/constants";
import {
  getGuestAwareHeaders,
  getGuestId,
  storeGuestIdFromHeaders,
} from "../../utils/guestSession";
import {
  hasBenefitsSession,
  startBenefitsGuestSession,
} from "../../utils/benefitsSession";
import "./BenefitsExperience.css";

const BenefitsAccess = () => {
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Benefits Screening | NClude";
    headingRef.current?.focus();
  }, []);

  if (hasBenefitsSession()) {
    return <Navigate to="/" replace />;
  }

  const startScreening = async (event: FormEvent) => {
    event.preventDefault();
    if (isStarting) return;

    const normalizedName = name.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedName || !nameInputRef.current?.checkValidity()) {
      nameInputRef.current?.focus();
      nameInputRef.current?.reportValidity();
      return;
    }

    if (!normalizedEmail || !emailInputRef.current?.checkValidity()) {
      emailInputRef.current?.focus();
      emailInputRef.current?.reportValidity();
      return;
    }

    const payload = {
      name: normalizedName,
      email: normalizedEmail,
    };

    setIsStarting(true);
    setError("");

    try {
      const headers = getGuestAwareHeaders();
      const guestId = getGuestId();
      if (guestId) headers["X-Guest-Id"] = guestId;

      const response = await fetch(START_BENEFITS, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      storeGuestIdFromHeaders(response.headers);

      if (!response.ok) {
        let message = "We couldn’t start your screening. Please try again.";
        try {
          const result = await response.json();
          message = result?.message || result?.error || result?.data?.message || message;
        } catch {
          // Keep the friendly fallback when the response has no JSON body.
        }
        throw new Error(message);
      }

      startBenefitsGuestSession();
      navigate("/chat", { replace: true });
    } catch (startError) {
      setError(
        startError instanceof Error
          ? startError.message
          : "We couldn’t start your screening. Please try again."
      );
      setIsStarting(false);
    }
  };

  return (
    <main className="benefits-access-page" id="main-content">
      <BenefitsBrandPanel />

      <section className="benefits-access-panel" aria-labelledby="benefits-access-heading">
        <div className="benefits-access-form-wrap">
          <h2 id="benefits-access-heading" ref={headingRef} tabIndex={-1}>
            Start your free screening
          </h2>
          <p className="benefits-access-subtitle">
            Name and email are required. Sharing them helps us understand who we’re
            supporting.
          </p>

          <form className="benefits-start-form" onSubmit={startScreening}>
            <div className="benefits-form-field">
              <label htmlFor="benefits-name">
                Name <span aria-hidden="true">*</span>
              </label>
              <input
                ref={nameInputRef}
                id="benefits-name"
                name="name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your name"
                autoComplete="name"
                disabled={isStarting}
                required
                aria-required="true"
              />
            </div>
            <div className="benefits-form-field">
              <label htmlFor="benefits-email">
                Email address <span aria-hidden="true">*</span>
              </label>
              <input
                ref={emailInputRef}
                id="benefits-email"
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                spellCheck={false}
                disabled={isStarting}
                required
                aria-required="true"
              />
            </div>
            <button
              type="submit"
              className="benefits-primary-button"
              disabled={isStarting}
              aria-busy={isStarting}
            >
              {isStarting ? "Starting…" : "Continue to Benefits Assistant"}
            </button>
          </form>

          {error && <p className="benefits-access-error" role="alert">{error}</p>}

          <p className="benefits-privacy-note">
            Any details you share are used to support your screening and improve access to
            benefits. Your information is never sold.
          </p>
        </div>
      </section>
    </main>
  );
};

export default BenefitsAccess;
